import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, tap, shareReplay } from 'rxjs/operators';
import { AppConfig } from '../app.config';

export interface UserMedal {
  id: string;
  userId: string;
  clientId: string;
  medal: 'GOLD' | 'SILVER' | 'BRONZE';
  ranking: number;
  interactionScore: number;
  awardedDate: string;
  active: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MedalsService {
  private service = AppConfig.data['services']['social-network'];
  private endpoint = `${this.service}/v1`;
  private medalCache = new Map<string, Observable<UserMedal | null>>();
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes
  private lastCacheClear = Date.now();

  constructor(private http: HttpClient) {
    // Clear cache periodically
    setInterval(() => {
      this.clearExpiredCache();
    }, this.cacheExpiry);
  }

  getUserMedal(userId: string): Observable<UserMedal | null> {
    if (!userId) {
      return of(null);
    }

    // Check cache
    if (this.medalCache.has(userId)) {
      return this.medalCache.get(userId)!;
    }

    // Make request and cache it
    const medal$ = this.http.get<UserMedal>(`${this.endpoint}/medals/user/${userId}`).pipe(
      catchError(() => of(null)),
      shareReplay(1)
    );

    this.medalCache.set(userId, medal$);
    return medal$;
  }

  clearCache(userId?: string): void {
    if (userId) {
      this.medalCache.delete(userId);
    } else {
      this.medalCache.clear();
    }
  }

  private clearExpiredCache(): void {
    const now = Date.now();
    if (now - this.lastCacheClear > this.cacheExpiry) {
      this.medalCache.clear();
      this.lastCacheClear = now;
    }
  }

  getMedalColor(medal: 'GOLD' | 'SILVER' | 'BRONZE'): string {
    switch (medal) {
      case 'GOLD':
        return '#FFD700';
      case 'SILVER':
        return '#C0C0C0';
      case 'BRONZE':
        return '#CD7F32';
      default:
        return '#808080';
    }
  }

  getMedalIcon(medal: 'GOLD' | 'SILVER' | 'BRONZE'): string {
    switch (medal) {
      case 'GOLD':
        return '🥇';
      case 'SILVER':
        return '🥈';
      case 'BRONZE':
        return '🥉';
      default:
        return '';
    }
  }
}